// frontend/src/components/AddressCascade.js
import React, { useState, useRef, useEffect, useCallback } from 'react';

const THEMES = {
  desktop: { input: { padding: '9px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14, background: 'var(--surface)', color: '#0f172a', outline: 'none' },
             menu: { background: 'var(--surface)', border: '1px solid #e5e7eb', color: '#0f172a' }, itemHover: '#eef2ff', muted: '#64748b' },
  mobile: { input: { padding: '11px 12px', borderRadius: 10, border: '1px solid var(--line)', fontSize: 15, background: 'var(--surface)', color: 'var(--ink)', outline: 'none', width: '100%' },
            menu: { background: 'var(--surface)', border: '1px solid var(--line)', color: 'var(--ink)' }, itemHover: 'var(--surface2)', muted: 'var(--muted)' },
  public: { input: { padding: '11px 12px', borderRadius: 12, border: '1px solid var(--line-2)', fontSize: 14, background: 'var(--surface-2)', color: 'var(--ink)', outline: 'none', width: '100%' },
            menu: { background: 'var(--panel)', border: '1px solid var(--line-2)', color: 'var(--ink)' }, itemHover: 'var(--surface-2)', muted: 'var(--muted)' },
};

function Field({ t, value, placeholder, disabled, minLen, fetch, onPick, renderItem, adornment, inputClass }) {
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const seq = useRef(0);
  const box = useRef(null);

  useEffect(() => { setQ(value || ''); }, [value]);
  useEffect(() => {
    const h = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  // Fix 2: cleanup del timer di debounce all'unmount
  useEffect(() => () => clearTimeout(timer.current), []);

  const cerca = useCallback((txt) => {
    clearTimeout(timer.current);
    if ((txt || '').trim().length < minLen) { setItems([]); setOpen(false); return; }
    // Fix 3: guardia di sequenza per risposte fetch fuori ordine
    const mySeq = ++seq.current;
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await fetch(txt.trim());
        if (seq.current === mySeq) { setItems(r || []); setOpen(true); }
      }
      catch { if (seq.current === mySeq) { setItems([]); setOpen(true); } }
      finally { setLoading(false); }
    }, 300);
  }, [fetch, minLen]);

  const onChange = (e) => { const txt = e.target.value; setQ(txt); cerca(txt); };
  const scegli = (it) => { onPick(it); setOpen(false); };

  return (
    <div ref={box} style={{ position: 'relative' }}>
      <input className={inputClass || undefined}
        style={inputClass
          ? { opacity: disabled ? 0.55 : 1, ...(adornment ? { paddingRight: 40 } : null) }
          : { ...t.input, opacity: disabled ? 0.55 : 1, ...(adornment ? { paddingRight: 40 } : null) }}
        value={q} placeholder={placeholder}
        disabled={disabled} onChange={onChange}
        onFocus={() => { if (minLen === 0) cerca(q); else if (items.length) setOpen(true); }} autoComplete="off" />
      {adornment}
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, marginTop: 4,
          borderRadius: 10, maxHeight: 240, overflowY: 'auto', ...t.menu,
          boxShadow: '0 20px 45px -20px rgba(0,0,0,.45)' }}>
          {loading && <div style={{ padding: '9px 12px', fontSize: 13, color: t.muted }}>Ricerca…</div>}
          {!loading && items.length === 0 && <div style={{ padding: '9px 12px', fontSize: 13, color: t.muted }}>Nessun risultato</div>}
          {!loading && items.map((it, i) => (
            <div key={(it.codCom || it.codForteVia || it.civico || '') + '-' + i} onMouseDown={(e) => { e.preventDefault(); scegli(it); }}
              style={{ padding: '9px 12px', fontSize: 13, cursor: 'pointer' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = t.itemHover)}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
              {renderItem(it)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AddressCascade({ fetcher, onChange, variant = 'desktop',
  stepper = false, onSubmit, submitLabel = 'Verifica', submitDisabled = false,
  nextLabel = 'Segue', btnClass = 'btn', submitDataTour, inputClass, rowLayout = false }) {
  const t = THEMES[variant] || THEMES.desktop;
  const [com, setCom] = useState(null);   // {nome,provincia,codProv,codCom}
  const [via, setVia] = useState(null);   // {via,codForteVia}
  const [civ, setCiv] = useState('');
  const [civici, setCivici] = useState([]);
  const [locating, setLocating] = useState(false);
  const [geoMsg, setGeoMsg] = useState('');
  const [step, setStep] = useState('comune');   // solo in modalita' stepper: 'comune' | 'via' | 'civico'

  const emit = useCallback((c, v, cv) => {
    onChange({ provincia: c ? c.provincia : '', comune: c ? c.nome : '',
      via: v ? v.via : '', civico: cv || '', complete: !!(c && v && cv) });
  }, [onChange]);

  const norm = (s) => (s || '').trim().toLowerCase();

  // Aggancia una posizione (lat/lon) a comune/via/civico della toponomastica TIM.
  const risolviPosizione = async (pos) => {
    try {
      const addr = await fetcher.reverseGeo(pos.coords.latitude, pos.coords.longitude);
      if (!addr || !addr.comune) { setGeoMsg('Indirizzo non trovato per la tua posizione'); return; }
      const comuni = await fetcher.comuni(addr.comune).catch(() => []);
      const c = comuni.find((x) => norm(x.nome) === norm(addr.comune))
        || comuni.find((x) => norm(x.nome).startsWith(norm(addr.comune))) || comuni[0];
      if (!c) { setGeoMsg(`Comune "${addr.comune}" non in toponomastica`); return; }
      setCom(c); setVia(null); setCiv(''); setCivici([]); emit(c, null, '');
      let v = null, cv = '';
      if (addr.via) {
        const vie = await fetcher.vie(addr.via, c.codProv, c.codCom).catch(() => []);
        v = vie.find((x) => norm(x.via) === norm(addr.via))
          || vie.find((x) => norm(x.via).includes(norm(addr.via)) || norm(addr.via).includes(norm(x.via)))
          || vie[0] || null;
        if (v) {
          setVia(v);
          const cl = await fetcher.civici(v.codForteVia).catch(() => []);
          setCivici(cl);
          if (addr.civico) {
            const cc = cl.find((x) => norm(x.civico) === norm(addr.civico));
            if (cc) cv = cc.civico;
          }
          setCiv(cv);
        }
      }
      emit(c, v, cv);
      if (stepper) setStep(v ? 'civico' : 'via');   // geoloc: salta al passo piu' avanzato risolto
      setGeoMsg(!v ? 'Via da completare a mano' : (addr.civico && !cv ? 'Civico da confermare' : ''));
    } catch { setGeoMsg('Errore durante la geolocalizzazione'); }
    finally { setLocating(false); }
  };

  // Geolocalizza aspettando il fix PIU' PRECISO. La prima posizione del telefono e' spesso quella
  // grezza da rete/wifi (±50-100 m) e cade nella via accanto; il GPS si affina in pochi secondi.
  // Con watchPosition prendo il fix con accuratezza migliore (o <=25 m) prima di risolvere l'indirizzo.
  const localizza = () => {
    if (!navigator.geolocation) { setGeoMsg('Geolocalizzazione non supportata dal browser'); return; }
    // Fuori da https/localhost i browser bloccano la posizione senza nemmeno mostrare il popup.
    if (window.isSecureContext === false) {
      setGeoMsg('Per la posizione apri l\'app in https:// (su http di rete locale il telefono la blocca).');
      return;
    }
    setLocating(true); setGeoMsg('Localizzo…');
    let best = null, done = false, wid = null, to = null;
    const stop = () => { if (wid != null) { try { navigator.geolocation.clearWatch(wid); } catch (e) {} } clearTimeout(to); };
    wid = navigator.geolocation.watchPosition((pos) => {
      if (!best || pos.coords.accuracy < best.coords.accuracy) best = pos;
      setGeoMsg(`Localizzo… (±${Math.round(pos.coords.accuracy)} m, attendo il GPS…)`);
      if (!done && pos.coords.accuracy <= 25) { done = true; stop(); risolviPosizione(pos); }
    }, (err) => {
      if (done) return; done = true; stop(); setLocating(false);
      if (err && err.code === 1) setGeoMsg('Posizione negata: attivala dalle Impostazioni del telefono (Privacy > Localizzazione, e per il browser), poi riprova.');
      else if (err && err.code === 3) setGeoMsg('Posizione: tempo scaduto, riprova.');
      else setGeoMsg('Posizione non disponibile: attiva la localizzazione del telefono e riprova.');
    }, { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 });
    // Dopo un po', se non e' arrivato un fix preciso, usa comunque il migliore raccolto.
    to = setTimeout(() => {
      if (done) return; done = true; stop();
      if (best) risolviPosizione(best);
      else { setLocating(false); setGeoMsg('Posizione poco precisa: riprova all\'aperto.'); }
    }, 9000);
  };

  const locateBtn = fetcher.reverseGeo ? (
    <button type="button" data-tour="cov-geo" onClick={localizza} disabled={locating} title="Usa la mia posizione"
      style={{ position: 'absolute', right: 5, top: 5, bottom: 5, width: 32, borderRadius: 8, border: 'none',
        background: 'transparent', color: t.muted, cursor: locating ? 'default' : 'pointer',
        display: 'grid', placeItems: 'center', opacity: locating ? 0.5 : 1 }}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="3" /><line x1="12" y1="1.5" x2="12" y2="4" /><line x1="12" y1="20" x2="12" y2="22.5" /><line x1="1.5" y1="12" x2="4" y2="12" /><line x1="20" y1="12" x2="22.5" y2="12" /></svg>
    </button>
  ) : null;

  const pickComune = (c) => { setCom(c); setVia(null); setCiv(''); setCivici([]); setGeoMsg(''); emit(c, null, ''); };
  const pickVia = async (v) => {
    setVia(v); setCiv(''); emit(com, v, '');
    try { setCivici(await fetcher.civici(v.codForteVia)); } catch { setCivici([]); }
  };
  const pickCivico = (it) => { setCiv(it.civico); emit(com, via, it.civico); };

  // Campi (riusati sia nel layout a 3 righe sia nello stepper)
  const comuneField = (
    <Field t={t} placeholder="Comune" minLen={2} inputClass={inputClass}
      value={com ? com.nome : ''}
      fetch={(q) => fetcher.comuni(q)}
      onPick={pickComune}
      adornment={locateBtn}
      renderItem={(c) => `${c.nome} (${c.siglaProv})`} />
  );
  const viaField = (
    <Field t={t} placeholder="Via / indirizzo" minLen={2} disabled={!com} inputClass={inputClass}
      value={via ? via.via : ''}
      fetch={(q) => fetcher.vie(q, com.codProv, com.codCom)}
      onPick={pickVia}
      renderItem={(v) => v.via + (v.cap ? ` · ${v.cap}` : '')} />
  );
  const civicoField = (
    <Field t={t} placeholder="Civico" minLen={0} disabled={!via} inputClass={inputClass}
      value={civ}
      fetch={(q) => Promise.resolve(
        civici.filter((c) => c.civico.toUpperCase().startsWith((q || '').toUpperCase())))}
      onPick={pickCivico}
      renderItem={(c) => c.civico} />
  );

  // Layout classico: le tre righe impilate (desktop/mobile/pagine dedicate senza stepper).
  if (!stepper) {
    // rowLayout: comune · via · civico sulla STESSA riga (finestra più compatta), con etichetta sopra.
    if (rowLayout) {
      const lblStyle = { fontSize: 10.5, fontWeight: 700, letterSpacing: '.03em', textTransform: 'uppercase', color: 'rgba(255,255,255,.5)', marginBottom: 3 };
      return (
        <div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <div style={{ flex: 1.4, minWidth: 0 }}><div style={lblStyle}>Comune</div>{comuneField}</div>
            <div style={{ flex: 1.4, minWidth: 0 }}><div style={lblStyle}>Indirizzo</div>{viaField}</div>
            <div style={{ flex: '0 0 96px' }}><div style={lblStyle}>Civico</div>{civicoField}</div>
          </div>
          {geoMsg && <div style={{ fontSize: 11.5, color: t.muted, marginTop: 6 }}>{geoMsg}</div>}
        </div>
      );
    }
    return (
      <div style={{ display: 'grid', gap: 8 }}>
        {comuneField}
        {viaField}
        {civicoField}
        {geoMsg && <div style={{ fontSize: 11.5, color: t.muted }}>{geoMsg}</div>}
      </div>
    );
  }

  // Stepper: un campo alla volta (Comune -> Segue -> Indirizzo -> Segue -> Civico -> Verifica).
  // Niente breadcrumb sopra (sfalsava la riga): al passo Civico, scelto il civico, il campo
  // diventa un UNICO campo con l'indirizzo completo (citta, via, civico) sulla stessa riga del tasto.
  const onNext = () => {
    if (step === 'comune' && com) setStep('via');
    else if (step === 'via' && via) setStep('civico');
    else if (step === 'civico' && civ && onSubmit) {
      onSubmit();   // avvia la verifica con l'indirizzo corrente (letto dal parent via onChange)
      // poi RESET: torna al Comune (campo libero + pulsante localizza), pronto per un nuovo indirizzo
      setCom(null); setVia(null); setCiv(''); setCivici([]); setGeoMsg(''); setStep('comune');
      emit(null, null, '');
    }
  };
  const nextDisabled = step === 'comune' ? !com : step === 'via' ? !via : (!civ || submitDisabled);
  const btnLabel = step === 'civico' ? submitLabel : nextLabel;
  const indirizzoCompleto = com && via ? `${com.nome}, ${via.via}${civ ? ` ${civ}` : ''}` : '';
  // Al passo Civico: prima scegli il civico; una volta scelto, mostra l'indirizzo completo
  // in un solo campo (di sola lettura). Tocca per cambiare il civico.
  const civicoStep = (step === 'civico' && civ) ? (
    <input readOnly value={indirizzoCompleto} title="Tocca per cambiare il civico"
      className={inputClass || undefined}
      style={inputClass ? { width: '100%', cursor: 'pointer' } : { ...t.input, cursor: 'pointer' }}
      onClick={() => { setCiv(''); emit(com, via, ''); }} />
  ) : civicoField;

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {rowLayout ? (
        // Campo + tasto sulla stessa riga (come "Verifica numero"): cambiando tab l'impaginazione non salta.
        <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {step === 'comune' ? comuneField : step === 'via' ? viaField : civicoStep}
          </div>
          <button type="button" className={btnClass} data-tour={submitDataTour}
            disabled={nextDisabled} onClick={onNext} style={{ flex: '0 0 auto' }}>{btnLabel}</button>
        </div>
      ) : (
        <>
          {step === 'comune' && comuneField}
          {step === 'via' && viaField}
          {step === 'civico' && civicoStep}
          <button type="button" className={btnClass} data-tour={submitDataTour}
            disabled={nextDisabled} onClick={onNext}>{btnLabel}</button>
        </>
      )}
      {geoMsg && <div style={{ fontSize: 11.5, color: t.muted }}>{geoMsg}</div>}
    </div>
  );
}
